import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatMessage } from './ChatMessage';
import type { ISteramMessage } from '../../../types';
import styles from './Chat.module.scss';
import { cleanAdminName } from '../../../utils/adminNameUtils';
import { SendIcon } from '../../../components/icons';

interface ChatProps {
    userName: string;
    roomId?: string;
    messages: ISteramMessage[];
    onNewMessage: (message: string) => void;
    isFullscreen?: boolean;
}

export const Chat: React.FC<ChatProps> = ({ userName, messages, onNewMessage, isFullscreen = false }) => {
    const { t } = useTranslation();
    const [newMessage, setNewMessage] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Автоскролл к последнему сообщению
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Отправка сообщения
    const sendMessage = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        onNewMessage(newMessage.trim());
        setNewMessage('');
    };

    const containerClass = isFullscreen ? styles.fullscreenContainer : styles.container;
    const messagesContainerClass = isFullscreen ? styles.fullscreenMessagesContainer : styles.messagesContainer;
    const formClass = isFullscreen ? styles.fullscreenForm : styles.form;
    const inputClass = isFullscreen ? styles.fullscreenInput : styles.input;
    const buttonClass = isFullscreen ? styles.fullscreenButton : styles.button;

    return (
        <div className={containerClass}>
            {!isFullscreen && <h4 className={styles.title}>{t('livestream.chat.title')}</h4>}
            <div className={messagesContainerClass}>
                {messages.length === 0 ? (
                    <div className={styles.noMessages}>
                        {t('livestream.chat.noMessages')}
                    </div>
                ) : (
                    messages.map(msg => {
                        // Убираем корону и суффикс языка для сравнения
                        const cleanMsgUsername = cleanAdminName(msg.username.replace(/^👑\s*/, ''));
                        const cleanUserName = cleanAdminName(userName);
                        const isOwnMessage = cleanMsgUsername === cleanUserName;

                        return (
                            <ChatMessage
                                key={msg.id}
                                message={msg}
                                isOwnMessage={isOwnMessage}
                                isFullscreen={isFullscreen}
                            />
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>
            <form onSubmit={sendMessage} className={formClass}>
                <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={isFullscreen ? "Текст сообщения" : t('livestream.chat.placeholder')}
                    className={inputClass}
                    maxLength={500}
                />
                <button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className={buttonClass}
                >
                    {isFullscreen ? <SendIcon /> : t('livestream.chat.send')}
                </button>
            </form>
        </div>
    );
}; 